import * as api from '../api';
import {
  WebRequestStatus,
  BaseOrganization,
  BaseUser,
  MemberRole,
  OrgMembership,
  OrgRole
} from '@/models';
import { defineModule, localActionContext } from 'direct-vuex';
import * as Utils from '@/utils';
// Erm, circular dependency here?
import {
  OrgStructure,
  OrgPacket,
  OrgPeerGroup,
  OrgUser,
  buildFromApi
} from '@/models/orgStructure';
import { UserInfo } from './auth';
import { Objective, Outcome, BasePacket, Badge } from '@/models/packet';
import { IconData } from '@/icons';
import { makeWebRequest } from '@/utils';
import { GoogleClassroomCourse } from '@/api/auth/google';
import {
  CopiedSettings,
  DuplicateParts,
  DuplicateProgress,
  DuplicateResult,
  copyIsIncomplete,
  membersToCopy
} from '@/peerGroupDuplicate';

interface ActiveRequests {
  user: Utils.RequestTrackerById;
  group: Utils.RequestTrackerById;
  relationship: Utils.RequestTrackerByKey;
  objective: Utils.RequestTrackerById;
  outcome: Utils.RequestTrackerById;

  // The above are self explanatory, but create is used to track requests
  // for models which do not (yet) have an associated id.
  // The id can/should be generated from the component which dispatches the action,
  // and can/should use the `idGenerator` which will limit the risk of id collision.
  create: Utils.RequestTrackerById;

  // Technically, we could totally reuse create for this purpose too.
  //
  // Associate is for tracking the status of requests which are linking two models
  // (for now, peerGroups and packets).
  //
  // The joining component should generate a tempId to track the status.
  associate: Utils.RequestTrackerById;

  orgUpdate: WebRequestStatus;
  ltiRegistration: WebRequestStatus;
}

interface State {
  basic: BaseOrganization;
  org: OrgStructure;
  status: WebRequestStatus;
  userInfo: UserInfo;
  activeRequests: ActiveRequests;
  requestTracker: Utils.RequestTrackerById;

  // Role the user has in the org
  orgAdmin: boolean;
  orgOrganizer: boolean;

  // Access allowed
  //
  // May be different than role (e.g. superusers)
  adminAccess: boolean;
  organizerAccess: boolean;

  publicPackets: [BasePacket, Badge[]][];
  errorMessages: string[];
}

type ReadyDelay<T> = {
  delay?: number;
} & T;

const emptyOrg = {
  organizationId: -1,
  name: '',
  createdAt: new Date(),
  roleNameStudent: '',
  roleNameStudentPlural: '',
  roleNameTeacher: '',
  roleNameTeacherPlural: ''
};

const getInitialState = (): State => {
  return {
    basic: emptyOrg,
    org: OrgStructure.empty(),
    status: WebRequestStatus.READY,
    userInfo: {
      isSuperAdmin: false,
      userId: -1,
      email: '',
      emails: []
    },
    activeRequests: {
      user: new Utils.RequestTrackerById({}),
      group: new Utils.RequestTrackerById({}),
      relationship: new Utils.RequestTrackerByKey({}),
      objective: new Utils.RequestTrackerById({}),
      outcome: new Utils.RequestTrackerById({}),
      create: new Utils.RequestTrackerById({}),
      associate: new Utils.RequestTrackerById({}),
      orgUpdate: WebRequestStatus.READY,
      ltiRegistration: WebRequestStatus.READY
    },
    requestTracker: new Utils.RequestTrackerById({}),
    orgAdmin: false,
    orgOrganizer: false,
    adminAccess: false,
    organizerAccess: false,
    publicPackets: [],
    errorMessages: []
  };
};

const getOrThrow = <T>(
  items: T[],
  matchFn: (item: T) => boolean
): [T, number] => {
  const index = items.findIndex(matchFn);
  const item = items[index];

  if (item === undefined) {
    throw new Error('No item found');
  }

  return [item, index];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const orgsActionContext = (context: any) =>
  // eslint-disable-next-line  @typescript-eslint/no-use-before-define
  localActionContext(context, organizations);

// Gives a member who was just created their org role.  On failure they stay
// in the org as a member, and the error says so; returns whether it worked.
const setNewMemberRole = async (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  context: any,
  newMember: BaseUser,
  email: string,
  role: OrgRole.ADMIN | OrgRole.ORGANIZER
): Promise<boolean> => {
  const roleFlags = {
    isAdmin: role === OrgRole.ADMIN,
    isOrganizer: role === OrgRole.ORGANIZER
  };
  const { state, commit } = orgsActionContext(context);

  try {
    const memberships = await api.secure.updateOrganizationMember(
      state.basic.organizationId,
      {
        member: newMember,
        ...roleFlags,
        isHomeOrg: true
      }
    );

    commit.setOrg(
      state.org.updateMemberships(
        memberships.map(m => ({ ...m, isOrphan: false }))
      )
    );

    return true;
  } catch (error) {
    console.error(error);

    commit.setOrg(
      state.org.updateUserRoleFlags(newMember.userId, {
        isAdmin: false,
        isOrganizer: false
      })
    );

    const roleName = role === OrgRole.ADMIN ? 'an admin' : 'an organizer';

    commit.errorMessagesAdd(
      `${email} was added as a member, but we couldn't make them ${roleName}. ` +
        'You can change their role from the Members list.'
    );

    return false;
  }
};

const organizations = defineModule({
  namespaced: true as true,
  state: getInitialState(),
  getters: {
    peerGroup: (state): ((peerGroupId: number) => OrgPeerGroup) => {
      const peerGroups = state.org.peerGroups;

      return (peerGroupId: number) => {
        const [peerGroup] = getOrThrow(
          peerGroups,
          p => p.peerGroupId === peerGroupId
        );

        return peerGroup;
      };
    },

    user: (state): ((userId: number) => OrgUser) => {
      const users = state.org.users;

      return (userId: number) => {
        const [user] = getOrThrow(users, u => u.userId === userId);

        return user;
      };
    },

    packet: (state): ((packetId: number) => OrgPacket) => {
      const packets = state.org.packets;

      return (packetId: number) => {
        const [packet] = getOrThrow(
          packets,
          p => p.packet.packetId === packetId
        );

        return packet;
      };
    },

    publicPacket: (state): ((packetId: number) => [BasePacket, Badge[]]) => {
      return (packetId: number) => {
        const [publicPacket] = getOrThrow(
          state.publicPackets,
          ([pp]) => pp.packetId === packetId
        );

        return publicPacket;
      };
    },

    status: state => {
      return {
        user: (userId: number) => state.activeRequests.user.getStatus(userId),
        group: (peerGroupId: number) =>
          state.activeRequests.group.getStatus(peerGroupId),
        relationship: (userId: number, peerGroupId: number) =>
          state.activeRequests.relationship.getStatus(
            `${userId}-${peerGroupId}`
          ),
        objective: (objectiveId: number) =>
          state.activeRequests.objective.getStatus(objectiveId),
        outcome: (outcomeId: number) =>
          state.activeRequests.outcome.getStatus(outcomeId),
        create: (tempCreateId: number) =>
          state.activeRequests.create.getStatus(tempCreateId),
        associate: (tempAssociateId: number) =>
          state.activeRequests.associate.getStatus(tempAssociateId),
        orgUpdate: () => state.activeRequests.orgUpdate,
        ltiRegistration: () => state.activeRequests.ltiRegistration
      };
    },

    requestStatus: state => {
      return (trackingId: number, defaultStatus = WebRequestStatus.READY) =>
        state.requestTracker.getStatus(trackingId) || defaultStatus;
    },

    isTeacher: state => {
      return state.org.peerGroups
        .flatMap(pg => pg.members)
        .filter(member => member.role === MemberRole.TEACHER)
        .some(member => member.userId === state.userInfo.userId);
    },

    canUpdateMemberName: (state, getters) => (memberId: number) => {
      if (!getters.user(memberId).isHomeOrg) {
        return false;
      }

      if (state.userInfo.isSuperAdmin) return true;
      if (state.orgAdmin) return true;

      return state.org.peerGroups.some(pg => {
        const hasMember = pg.members.some(m => m.userId === memberId);
        const isTeacher = pg.members.some(
          m =>
            m.userId === state.userInfo.userId && m.role === MemberRole.TEACHER
        );
        return hasMember && isTeacher;
      });
    }
  },
  mutations: {
    reset: state => {
      const empty = getInitialState();

      state.activeRequests = empty.activeRequests;
      state.adminAccess = empty.adminAccess;
      state.organizerAccess = empty.organizerAccess;
      state.orgAdmin = empty.orgAdmin;
      state.orgOrganizer = empty.orgOrganizer;
      state.basic = empty.basic;
      state.errorMessages = empty.errorMessages;
      state.org = empty.org;
      state.publicPackets = empty.publicPackets;
      state.requestTracker = empty.requestTracker;
      state.status = empty.status;
      state.userInfo = empty.userInfo;
    },
    errorMessagesAdd: (state, message: string) => {
      state.errorMessages = [...state.errorMessages, message];
    },
    errorMessagesRemove: (state, index: number) => {
      state.errorMessages = state.errorMessages.reduce(
        (acc: string[], message, i) => (i === index ? acc : [...acc, message]),
        []
      );
    },
    publicPacketsSet: (state, packets: [BasePacket, Badge[]][]) => {
      state.publicPackets = packets;
    },
    updateOrgUpdateStatus: (state, status: WebRequestStatus) => {
      state.activeRequests = {
        ...state.activeRequests,
        orgUpdate: status
      };
    },
    updateLtiRegistrationStatus: (state, status: WebRequestStatus) => {
      state.activeRequests = {
        ...state.activeRequests,
        ltiRegistration: status
      };
    },
    requestStartProgress: state => {
      state.status = WebRequestStatus.IN_PROGRESS;
    },
    requestFail: state => {
      state.status = WebRequestStatus.FAILURE;
    },
    requestReset: state => {
      state.status = WebRequestStatus.READY;
    },
    requestSucceed: state => {
      state.status = WebRequestStatus.SUCCESS;
    },
    setBasicInfo: (state, basic: BaseOrganization) => {
      state.basic = basic;
    },
    setOrg: (state, org: OrgStructure) => {
      state.org = org;
    },
    setUserInfo: (state, userInfo: UserInfo) => {
      state.userInfo = userInfo;
    },
    setActiveRequests: (state, activeRequests: ActiveRequests) => {
      state.activeRequests = activeRequests;
    },
    setOrgAdmin: (state, orgAdmin: boolean) => {
      state.orgAdmin = orgAdmin;
    },
    setAdminAccess: (state, adminAccess: boolean) => {
      state.adminAccess = adminAccess;
    },
    setOrgOrganizer: (state, orgOrganizer: boolean) => {
      state.orgOrganizer = orgOrganizer;
    },
    setOrganizerAccess: (state, organizerAccess: boolean) => {
      state.organizerAccess = organizerAccess;
    },
    upsertUserStatus: (
      state,
      { userId, status }: { userId: number; status: WebRequestStatus }
    ) => {
      state.activeRequests = {
        ...state.activeRequests,
        user: state.activeRequests.user.setStatus(userId, status)
      };
    },
    upsertObjectiveStatus: (
      state,
      { objectiveId, status }: { objectiveId: number; status: WebRequestStatus }
    ) => {
      state.activeRequests = {
        ...state.activeRequests,
        objective: state.activeRequests.objective.setStatus(objectiveId, status)
      };
    },
    upsertOutcomeStatus: (
      state,
      { outcomeId, status }: { outcomeId: number; status: WebRequestStatus }
    ) => {
      state.activeRequests = {
        ...state.activeRequests,
        outcome: state.activeRequests.outcome.setStatus(outcomeId, status)
      };
    },
    upsertPeerGroupStatus: (
      state,
      { peerGroupId, status }: { peerGroupId: number; status: WebRequestStatus }
    ) => {
      state.activeRequests = {
        ...state.activeRequests,
        group: state.activeRequests.group.setStatus(peerGroupId, status)
      };
    },
    upsertRelationshipStatus: (
      state,
      {
        userId,
        peerGroupId,
        status
      }: { userId: number; peerGroupId: number; status: WebRequestStatus }
    ) => {
      state.activeRequests = {
        ...state.activeRequests,
        relationship: state.activeRequests.relationship.setStatus(
          `${userId}-${peerGroupId}`,
          status
        )
      };
    },
    upsertCreateStatus: (
      state,
      {
        tempCreateId,
        status
      }: { tempCreateId: number; status: WebRequestStatus }
    ) => {
      state.activeRequests = {
        ...state.activeRequests,
        create: state.activeRequests.create.setStatus(tempCreateId, status)
      };
    },
    upsertAssociateStatus: (
      state,
      {
        tempAssociateId,
        status
      }: { tempAssociateId: number; status: WebRequestStatus }
    ) => {
      state.activeRequests = {
        ...state.activeRequests,
        associate: state.activeRequests.associate.setStatus(
          tempAssociateId,
          status
        )
      };
    },
    upsertRequestStatus: (
      state,
      { trackingId, status }: { trackingId: number; status: WebRequestStatus }
    ) => {
      state.requestTracker = state.requestTracker.setStatus(trackingId, status);
    }
  },

  actions: {
    async updateMemberName(
      context,
      payload: { memberId: number; name: string }
    ) {
      const { commit, state } = orgsActionContext(context);
      const organizationId = state.basic.organizationId;
      commit.upsertUserStatus({
        userId: payload.memberId,
        status: WebRequestStatus.IN_PROGRESS
      });

      try {
        await api.secure.updateOrganizationMemberName(
          organizationId,
          payload.memberId,
          payload.name
        );

        const member = state.org.users.find(m => m.userId === payload.memberId);
        if (member) {
          const updatedUser = { ...member, name: payload.name };
          commit.setOrg(state.org.updateUser(updatedUser));
        }

        commit.upsertUserStatus({
          userId: payload.memberId,
          status: WebRequestStatus.SUCCESS
        });
        await Utils.sleep(1000);
      } catch (error) {
        commit.errorMessagesAdd('Failed to update member name.');
        commit.upsertUserStatus({
          userId: payload.memberId,
          status: WebRequestStatus.FAILURE
        });
        await Utils.sleep(4000);
      }
      commit.upsertUserStatus({
        userId: payload.memberId,
        status: WebRequestStatus.READY
      });
    },
    async initOrg(
      context,
      { orgId, userInfo }: { orgId: number; userInfo: UserInfo }
    ) {
      const { commit } = orgsActionContext(context);

      commit.requestStartProgress();
      commit.setUserInfo(userInfo);

      try {
        const [
          groups,
          allOrgs,
          orgMembers,
          packets,
          publicPackets
        ] = await Promise.all([
          api.secure.getOrganizationStructure(orgId),
          api.secure.getUserOrganizations(),
          api.secure.getOrganizationMembers(orgId),
          api.secure.getOrganizationPackets(orgId),
          api.public.getPublicPackets()
        ]);

        const [currentOrg] = getOrThrow(
          allOrgs,
          o => o.organizationId == orgId
        );

        const orgStructure = buildFromApi(groups, orgMembers, packets);

        const currentUser = orgMembers.find(
          om => om.member.userId === userInfo.userId
        );

        const orgAdmin = !!currentUser?.isAdmin;
        const orgOrganizer = !!currentUser?.isOrganizer;

        // Check if the user has admin access to this group, or is a super admin
        const adminAccess = orgAdmin || userInfo.isSuperAdmin;

        commit.setOrgAdmin(orgAdmin);
        commit.setOrgOrganizer(orgOrganizer);
        commit.setAdminAccess(adminAccess);
        commit.setOrganizerAccess(orgOrganizer);

        commit.requestSucceed();
        commit.setBasicInfo(currentOrg);
        commit.setOrg(orgStructure);
        commit.publicPacketsSet(publicPackets);
      } catch (error) {
        console.error(error);
        commit.requestFail();
      }
    },

    async initIfDifferentOrg(
      context,
      payload: { orgId: number; userInfo: UserInfo }
    ) {
      const { state } = orgsActionContext(context);

      if (payload.orgId !== state.basic.organizationId) {
        organizations.actions.initOrg(context, payload);
      }
    },

    async updateUserRoles(context, orgUser: OrgUser) {
      //
      const { state, commit } = orgsActionContext(context);

      const membership: OrgMembership = {
        member: orgUser,
        isAdmin: orgUser.isAdmin,
        isOrganizer: orgUser.isOrganizer,
        isOrphan: orgUser.isOrphan,
        isHomeOrg: orgUser.isHomeOrg
      };

      commit.upsertUserStatus({
        userId: membership.member.userId,
        status: WebRequestStatus.IN_PROGRESS
      });

      try {
        const memberships = await api.secure.updateOrganizationMember(
          state.basic.organizationId,
          membership
        );

        commit.upsertUserStatus({
          userId: membership.member.userId,
          status: WebRequestStatus.SUCCESS
        });

        commit.setOrg(
          state.org.updateMemberships(
            memberships.map(m => ({ ...m, isOrphan: false }))
          )
        );

        await Utils.sleep(1000);
      } catch (error) {
        console.error(error);

        commit.upsertUserStatus({
          userId: membership.member.userId,
          status: WebRequestStatus.FAILURE
        });

        await Utils.sleep(3000);
      }

      commit.upsertUserStatus({
        userId: membership.member.userId,
        status: WebRequestStatus.READY
      });
    },

    // 1. Add placeholder user to org (email only)
    // 2. Get back members
    // 3. Find user in members
    // 4. Update placeholder user
    // 5. Set the org role, when it's other than member
    async addNewUserToOrg(
      context,
      payload: ReadyDelay<{ email: string; role?: OrgRole }>
    ) {
      const { state, commit } = orgsActionContext(context);
      const readyDelay = payload.delay || 1000;
      const lowercasedEmail = payload.email.toLowerCase();
      const roleFlags = {
        isAdmin: payload.role === OrgRole.ADMIN,
        isOrganizer: payload.role === OrgRole.ORGANIZER
      };

      // Use a temporary user id until we get the real one back.
      let userId = Utils.idGenerator.next();

      // No need to create if the user exists
      if (!Utils.validateEmail(lowercasedEmail)) {
        console.error(`Not a valid email: ${lowercasedEmail}`);
        return;
      }

      if (state.org.users.some(u => u.email === lowercasedEmail)) {
        console.error(`User already exists: ${lowercasedEmail}`);
        return;
      }

      commit.upsertUserStatus({
        userId,
        status: WebRequestStatus.IN_PROGRESS
      });

      // Create a temporary user so that we can track the progress of this
      // request.  It carries the requested role so it stays visible in a list
      // filtered to that role.
      commit.setOrg(
        state.org.addUser(
          {
            userId,
            email: lowercasedEmail,
            emails: []
          },
          roleFlags
        )
      );

      try {
        const members = await api.secure.upsertUserIntoOrganization(
          state.basic.organizationId,
          lowercasedEmail
        );

        const [newMember] = getOrThrow(members, m =>
          m.emails.some(ue => ue.email.toLowerCase() === lowercasedEmail)
        );

        // Reset the temporary id request status.
        // Might not actually need to do this because the id should never
        // be re-used.
        commit.upsertUserStatus({
          userId,
          status: WebRequestStatus.READY
        });

        // Update the userId of the temporary user we created
        // Update the user
        commit.setOrg(
          state.org.updateUserId(userId, newMember.userId).updateUser(newMember)
        );

        // Use the new userId to track progress
        userId = newMember.userId;

        commit.upsertUserStatus({
          userId,
          status: WebRequestStatus.IN_PROGRESS
        });

        // The create endpoint takes no role, so a second call sets it.
        const roleSet =
          payload.role === OrgRole.ADMIN || payload.role === OrgRole.ORGANIZER
            ? await setNewMemberRole(
                context,
                newMember,
                lowercasedEmail,
                payload.role
              )
            : true;

        commit.upsertUserStatus({
          userId,
          status: roleSet ? WebRequestStatus.SUCCESS : WebRequestStatus.FAILURE
        });
      } catch (error) {
        console.error(error);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((error as any).response?.status === 422) {
          commit.errorMessagesAdd(
            `${lowercasedEmail} is already a member of a different org.  If you'd like them to join your org as well,` +
              'please email us at support@unrulr.com for further assistance.'
          );
        }

        commit.upsertUserStatus({
          userId: userId,
          status: WebRequestStatus.FAILURE
        });

        await Utils.sleep(3000);

        // Remove the temporary user we created to track progress
        commit.setOrg(state.org.removeTemporaryUser(userId));
      }

      setTimeout(
        () =>
          commit.upsertUserStatus({
            userId: userId,
            status: WebRequestStatus.READY
          }),
        readyDelay
      );
    },

    async deleteUser(
      context,
      payload: {
        memberId: number;
        beforeOrgReInit: () => void;
      }
    ) {
      const { commit, state, dispatch } = orgsActionContext(context);
      const { memberId, beforeOrgReInit } = payload;

      await makeWebRequest(
        () => api.secure.deleteUser(state.basic.organizationId, memberId),
        // We actually don't need to process anything here, but we do want to
        // pop the nav stack and re-init the org;
        () => {
          beforeOrgReInit();
          dispatch.initOrg({
            orgId: state.basic.organizationId,
            userInfo: state.userInfo
          });
        },
        status => commit.upsertUserStatus({ userId: memberId, status })
      );
    },

    async createPeerGroup(
      context,
      payload: {
        tempCreateId: number;
        name: string;
        section?: string;
        description?: string;
      }
    ): Promise<number> {
      const { commit, state } = orgsActionContext(context);

      let createdGroupId = -1;

      commit.upsertCreateStatus({
        tempCreateId: payload.tempCreateId,
        status: WebRequestStatus.IN_PROGRESS
      });

      try {
        const newGroup = await api.secure.createPeerGroup(
          state.basic.organizationId,
          payload
        );

        createdGroupId = newGroup.peerGroupId;

        commit.upsertCreateStatus({
          tempCreateId: payload.tempCreateId,
          status: WebRequestStatus.SUCCESS
        });

        await Utils.sleep(1000);

        const withPeerGroup = state.org.addPeerGroup(newGroup);

        // We automatically set the current user as a teacher via the api
        // if they are not an org admin.
        //
        // But, generally, let's copy in all the users returned by the
        // api call.
        const withGroupMembers = newGroup.memberships.reduce(
          (orgStructure, membership) => {
            return orgStructure.connectGroupToUser(
              newGroup.peerGroupId,
              membership.user.userId,
              membership.role
            );
          },
          withPeerGroup
        );

        commit.setOrg(withGroupMembers);
      } catch (error) {
        console.error(error);

        commit.upsertCreateStatus({
          tempCreateId: payload.tempCreateId,
          status: WebRequestStatus.FAILURE
        });

        await Utils.sleep(4000);
      }
      commit.upsertCreateStatus({
        tempCreateId: payload.tempCreateId,
        status: WebRequestStatus.READY
      });

      return createdGroupId;
    },

    async importClassroomCourse(
      context,
      payload: {
        tempCreateId: number;
        course: GoogleClassroomCourse;
      }
    ): Promise<number> {
      const { commit, state, dispatch } = orgsActionContext(context);
      let createdGroupId = -1;

      commit.upsertCreateStatus({
        tempCreateId: payload.tempCreateId,
        status: WebRequestStatus.IN_PROGRESS
      });

      try {
        createdGroupId = await api.secure.importClassroom(
          state.basic.organizationId,
          payload.course.id
        );

        commit.upsertCreateStatus({
          tempCreateId: payload.tempCreateId,
          status: WebRequestStatus.SUCCESS
        });

        // Instead of manually adding the peer group, teachers and students (but
        // mostly because we only parse ApiPeerGroups inside of orgStructure.buildFromApi
        // and I don't want to leak that), let's just refresh this whole org?
        dispatch.initOrg({
          orgId: state.basic.organizationId,
          userInfo: state.userInfo
        });

        await Utils.sleep(1000);
      } catch (error) {
        console.error(error);

        commit.upsertCreateStatus({
          tempCreateId: payload.tempCreateId,
          status: WebRequestStatus.FAILURE
        });

        dispatch.displayErrorMessage(
          'Failed to import Google Classroom course.  If you would like assistance, please contact us at support@unrulr.com'
        );

        await Utils.sleep(4000);
      }
      commit.upsertCreateStatus({
        tempCreateId: payload.tempCreateId,
        status: WebRequestStatus.READY
      });

      return createdGroupId;
    },

    async syncClassroomCourse(
      context,
      payload: { peerGroupId: number; isCreator: boolean }
    ) {
      const { commit, state, dispatch } = orgsActionContext(context);

      commit.upsertPeerGroupStatus({
        peerGroupId: payload.peerGroupId,
        status: WebRequestStatus.IN_PROGRESS
      });

      try {
        await api.secure.forceManualClassroomSync(
          state.basic.organizationId,
          payload.peerGroupId,
          payload.isCreator
        );

        commit.upsertPeerGroupStatus({
          peerGroupId: payload.peerGroupId,
          status: WebRequestStatus.SUCCESS
        });

        // See note on importClassroomCourse on why we refresh the entire org.
        dispatch.initOrg({
          orgId: state.basic.organizationId,
          userInfo: state.userInfo
        });

        await Utils.sleep(1000);
      } catch (error) {
        console.error(error);

        if (!payload.isCreator) {
          dispatch.displayErrorMessage(
            'We were unable to sync to google classroom.  This could be because of old or revoked classroom access.' +
              ' To refresh access, please ask the original creator of this course to hit the sync button. ' +
              ' If that is not feasible, or if you would like assistance, please contact us at support@unrulr.com.'
          );
        }

        commit.upsertPeerGroupStatus({
          peerGroupId: payload.peerGroupId,
          status: WebRequestStatus.FAILURE
        });

        await Utils.sleep(1000);
      }

      commit.upsertPeerGroupStatus({
        peerGroupId: payload.peerGroupId,
        status: WebRequestStatus.READY
      });
    },

    async updatePeerGroup(
      context,
      payload: {
        peerGroupId: number;
        name?: string;
        section?: string;
        description?: string;
        isActive?: boolean;
        creatorCanPublish?: boolean;
        teachersCanPublish?: boolean;
        submissionStatusIsInteractive?: boolean;
        studentsCanShareWithStudents?: boolean;
        organizationSyncAssignments?: boolean;
        roleNameStudent?: string | null;
        roleNameStudentPlural?: string | null;
        roleNameTeacher?: string | null;
        roleNameTeacherPlural?: string | null;
      }
    ) {
      const { commit, state } = orgsActionContext(context);

      commit.upsertPeerGroupStatus({
        peerGroupId: payload.peerGroupId,
        status: WebRequestStatus.IN_PROGRESS
      });

      try {
        const updated = await api.secure.updatePeerGroup(
          payload.peerGroupId,
          payload
        );

        commit.upsertPeerGroupStatus({
          peerGroupId: payload.peerGroupId,
          status: WebRequestStatus.SUCCESS
        });

        await Utils.sleep(1000);

        commit.setOrg(state.org.updatePeerGroup(updated));
      } catch (error) {
        console.error(error);

        commit.upsertPeerGroupStatus({
          peerGroupId: payload.peerGroupId,
          status: WebRequestStatus.FAILURE
        });

        await Utils.sleep(4000);
      }

      commit.upsertPeerGroupStatus({
        peerGroupId: payload.peerGroupId,
        status: WebRequestStatus.READY
      });
    },

    async deactivatePeerGroup(context, peerGroupId: number) {
      await orgsActionContext(context).dispatch.updatePeerGroup({
        peerGroupId,
        isActive: false
      });
    },

    async reactivatePeerGroup(context, peerGroupId: number) {
      await orgsActionContext(context).dispatch.updatePeerGroup({
        peerGroupId,
        isActive: true
      });
    },

    // Archives or restores many groups at once.  There's no bulk endpoint, so
    // this sends one update per group, a few at a time, and applies whatever
    // succeeded in a single commit.  Callers report the outcome themselves,
    // so there's no per-group status tracking or error message here.
    async setPeerGroupsActive(
      context,
      { peerGroupIds, isActive }: { peerGroupIds: number[]; isActive: boolean }
    ): Promise<{ succeeded: number[]; failed: number[] }> {
      const { commit, state } = orgsActionContext(context);

      const results = await Utils.mapInBatches(peerGroupIds, 5, peerGroupId =>
        api.secure.updatePeerGroup(peerGroupId, { isActive })
      );

      const succeeded: number[] = [];
      const failed: number[] = [];
      let org = state.org;

      results.forEach((result, i) => {
        if (result.status === 'fulfilled') {
          succeeded.push(peerGroupIds[i]);
          org = org.updatePeerGroup(result.value);
        } else {
          console.error(result.reason);
          failed.push(peerGroupIds[i]);
        }
      });

      if (succeeded.length > 0) {
        commit.setOrg(org);
      }

      return { succeeded, failed };
    },

    // Copies a group into a new one, and can archive the original.  There's
    // no clone endpoint, so this creates the group, then sends the settings,
    // each set of COGS and each member, a few at a time.  Only a failed create
    // rejects: once the copy exists, a failed request is in the result, and
    // the original is archived only if none failed.  Changes are applied in
    // one commit at the end, onto whatever the org is by then; one that can't
    // be applied here is skipped, since the server has it and the next org
    // load shows it.
    async duplicatePeerGroup(
      context,
      payload: {
        sourcePeerGroupId: number;
        name: string;
        section: string;
        description: string;
        settings: CopiedSettings | null;
        parts: DuplicateParts;
        archiveSource: boolean;
        onProgress?: (progress: DuplicateProgress) => void;
      }
    ): Promise<DuplicateResult> {
      const { commit, state } = orgsActionContext(context);
      const { sourcePeerGroupId, settings, parts } = payload;
      const changes: ((org: OrgStructure) => OrgStructure)[] = [];

      // A progress display that throws mustn't fail the copy, or mark a
      // request that worked as failed.
      const report = (progress: DuplicateProgress) => {
        try {
          payload.onProgress?.(progress);
        } catch (error) {
          console.error(error);
        }
      };

      const [source] = getOrThrow(
        state.org.peerGroups,
        pg => pg.peerGroupId === sourcePeerGroupId
      );

      report({ step: 'create', done: 0, total: 1 });

      const newGroup = await api.secure.createPeerGroup(
        state.basic.organizationId,
        {
          name: payload.name,
          section: payload.section,
          description: payload.description
        }
      );
      const peerGroupId = newGroup.peerGroupId;

      report({ step: 'create', done: 1, total: 1 });

      // As in createPeerGroup: the API may have added the creator already.
      // Kept apart from the group, so a creator this org doesn't know can't
      // keep the copy itself out of the store.
      changes.push(org => org.addPeerGroup(newGroup));
      newGroup.memberships.forEach(m =>
        changes.push(org =>
          org.connectGroupToUser(peerGroupId, m.user.userId, m.role)
        )
      );

      const result: DuplicateResult = {
        peerGroupId,
        settingsFailed: false,
        failedPackets: [],
        failedMembers: [],
        archived: false,
        archiveFailed: false
      };

      if (settings) {
        report({ step: 'settings', done: 0, total: 1 });

        try {
          const updated = await api.secure.updatePeerGroup(
            peerGroupId,
            settings
          );

          changes.push(org => org.updatePeerGroup(updated));
        } catch (error) {
          console.error(error);
          result.settingsFailed = true;
        }

        report({ step: 'settings', done: 1, total: 1 });
      }

      const packets = parts.cogs ? source.packets : [];

      if (packets.length > 0) {
        const results = await Utils.mapInBatchesWithProgress(
          packets,
          5,
          p => api.secure.connectPeerGroupToPacket(peerGroupId, p.packetId),
          done => report({ step: 'cogs', done, total: packets.length })
        );

        results.forEach((r, i) => {
          if (r.status === 'fulfilled') {
            changes.push(org =>
              org.connectGroupToPacket(peerGroupId, packets[i].packetId)
            );
          } else {
            console.error(r.reason);
            result.failedPackets.push(packets[i]);
          }
        });
      }

      const members = membersToCopy(
        source.members,
        parts,
        new Set(newGroup.memberships.map(m => m.user.userId))
      );

      if (members.length > 0) {
        const results = await Utils.mapInBatchesWithProgress(
          members,
          5,
          m => api.secure.addUserGroupConnection(m.userId, peerGroupId, m.role),
          done => report({ step: 'members', done, total: members.length })
        );

        results.forEach((r, i) => {
          const { userId, role } = members[i];

          if (r.status === 'fulfilled') {
            changes.push(org =>
              org.connectGroupToUser(peerGroupId, userId, role)
            );
          } else {
            console.error(r.reason);
            result.failedMembers.push(members[i]);
          }
        });
      }

      if (payload.archiveSource && !copyIsIncomplete(result)) {
        report({ step: 'archive', done: 0, total: 1 });

        try {
          const archived = await api.secure.updatePeerGroup(sourcePeerGroupId, {
            isActive: false
          });

          changes.push(org => org.updatePeerGroup(archived));
          result.archived = true;
        } catch (error) {
          console.error(error);
          result.archiveFailed = true;
        }

        report({ step: 'archive', done: 1, total: 1 });
      }

      commit.setOrg(
        changes.reduce((org, change) => {
          try {
            return change(org);
          } catch (error) {
            console.error(error);
            return org;
          }
        }, state.org)
      );

      return result;
    },

    // All users are created as students.  The role can easily be changed after
    // this request completes.
    async createUserForGroup(
      context,
      {
        peerGroupId,
        email,
        role
      }: { peerGroupId: number; email: string; role?: MemberRole }
    ) {
      const { state, dispatch } = orgsActionContext(context);

      const lowercasedEmail = email.toLowerCase();

      await dispatch.addNewUserToOrg({ email: lowercasedEmail });

      try {
        // userId should now be stored on the state.
        // If it isn't there, this will throw, which means that the
        // addNewUserToOrg call failed (and the UX of failure is handled there).
        const [{ userId }] = getOrThrow(
          state.org.users,
          u => u.email === lowercasedEmail
        );

        await dispatch.addUserGroupConnection({
          userId,
          peerGroupId,
          role: role || MemberRole.STUDENT
        });
      } catch (error) {
        console.error(`Couldn't add user to group: ${lowercasedEmail}`);
      }
    },

    async addUserGroupConnection(
      context,
      {
        userId,
        peerGroupId,
        role
      }: { userId: number; peerGroupId: number; role: MemberRole }
    ) {
      const { commit, state } = orgsActionContext(context);

      commit.upsertRelationshipStatus({
        userId,
        peerGroupId,
        status: WebRequestStatus.IN_PROGRESS
      });

      try {
        await api.secure.addUserGroupConnection(userId, peerGroupId, role);

        commit.upsertRelationshipStatus({
          userId,
          peerGroupId,
          status: WebRequestStatus.SUCCESS
        });

        commit.setOrg(state.org.connectGroupToUser(peerGroupId, userId, role));
      } catch (error) {
        console.error(error);

        commit.upsertRelationshipStatus({
          userId,
          peerGroupId,
          status: WebRequestStatus.FAILURE
        });

        await Utils.sleep(3000);
      }

      /// Run this in set timeout instead of Utils.sleep because we don't
      /// want to delay the next call when running in series.
      setTimeout(
        () =>
          commit.upsertRelationshipStatus({
            userId,
            peerGroupId,
            status: WebRequestStatus.READY
          }),
        1000
      );
    },

    async removeUserGroupConnection(
      context,
      {
        userId,
        peerGroupId,
        role
      }: { userId: number; peerGroupId: number; role: MemberRole }
    ) {
      const { commit, state } = orgsActionContext(context);

      commit.upsertRelationshipStatus({
        userId,
        peerGroupId,
        status: WebRequestStatus.IN_PROGRESS
      });

      try {
        await api.secure.removeUserGroupConnection(userId, peerGroupId, role);

        commit.upsertRelationshipStatus({
          userId,
          peerGroupId,
          status: WebRequestStatus.SUCCESS
        });

        await Utils.sleep(1000);

        commit.setOrg(state.org.disconnectGroupFromUser(peerGroupId, userId));
      } catch (error) {
        console.error(error);

        commit.upsertRelationshipStatus({
          userId,
          peerGroupId,
          status: WebRequestStatus.FAILURE
        });

        await Utils.sleep(3000);
      }

      commit.upsertRelationshipStatus({
        userId,
        peerGroupId,
        status: WebRequestStatus.READY
      });
    },

    async updateUserGroupConnection(
      context,
      {
        userId,
        peerGroupId,
        role
      }: {
        userId: number;
        peerGroupId: number;
        role: MemberRole;
      }
    ) {
      const { commit, state, getters } = orgsActionContext(context);

      commit.upsertRelationshipStatus({
        userId,
        peerGroupId,
        status: WebRequestStatus.IN_PROGRESS
      });

      try {
        const user = getters.user(userId);

        // look up existing roles
        const previousRoles = user.memberships
          .filter(m => m.peerGroupId === peerGroupId)
          .map(m => m.role);

        await api.secure.updateUserGroupConnection(
          userId,
          peerGroupId,
          previousRoles,
          role
        );

        commit.upsertRelationshipStatus({
          userId,
          peerGroupId,
          status: WebRequestStatus.SUCCESS
        });

        await Utils.sleep(1000);

        commit.setOrg(
          state.org.updateGroupUserConnection(peerGroupId, userId, role)
        );
      } catch (error) {
        console.error(error);

        commit.upsertRelationshipStatus({
          userId,
          peerGroupId,
          status: WebRequestStatus.FAILURE
        });

        await Utils.sleep(3000);
      }

      commit.upsertRelationshipStatus({
        userId,
        peerGroupId,
        status: WebRequestStatus.READY
      });
    },

    async updatePacketObjective(
      context,
      { packetId, objective }: { packetId: number; objective: Objective }
    ) {
      const { commit, state } = orgsActionContext(context);

      commit.upsertObjectiveStatus({
        objectiveId: objective.objectiveId,
        status: WebRequestStatus.IN_PROGRESS
      });

      try {
        const updated = await api.secure.updateObjective(packetId, objective);

        commit.setOrg(state.org.updatePacketObjective(packetId, updated));

        commit.upsertObjectiveStatus({
          objectiveId: objective.objectiveId,
          status: WebRequestStatus.SUCCESS
        });

        await Utils.sleep(1000);
      } catch (error) {
        console.error(error);

        commit.upsertObjectiveStatus({
          objectiveId: objective.objectiveId,
          status: WebRequestStatus.FAILURE
        });

        await Utils.sleep(4000);
      }
      commit.upsertObjectiveStatus({
        objectiveId: objective.objectiveId,
        status: WebRequestStatus.READY
      });
    },

    async updatePacketOutcome(
      context,
      { packetId, outcome }: { packetId: number; outcome: Outcome }
    ) {
      const { commit, state } = orgsActionContext(context);

      commit.upsertOutcomeStatus({
        outcomeId: outcome.outcomeId,
        status: WebRequestStatus.IN_PROGRESS
      });

      try {
        const updated = await api.secure.updateOutcome(packetId, outcome);

        commit.setOrg(state.org.updatePacketOutcome(packetId, updated));

        commit.upsertOutcomeStatus({
          outcomeId: outcome.outcomeId,
          status: WebRequestStatus.SUCCESS
        });
      } catch (error) {
        console.error(error);

        commit.upsertOutcomeStatus({
          outcomeId: outcome.outcomeId,
          status: WebRequestStatus.FAILURE
        });

        Utils.sleep(4000);
      }

      commit.upsertOutcomeStatus({
        outcomeId: outcome.outcomeId,
        status: WebRequestStatus.READY
      });
    },

    // Always inserts the goal at the end of the list for now.
    async createSinglePointGoal(
      context,
      payload: {
        tempCreateId: number;
        packetId: number;
        name: string;
        color: string;
        iconData: IconData;
        description: string;
      }
    ) {
      const { dispatch, getters } = orgsActionContext(context);

      const index = getters.packet(payload.packetId).packet.outcomes.length;

      dispatch.insertOutcome({
        ...payload,
        value: payload.name,
        badgeDescription: payload.description,
        index
      });
    },

    // Updates (in sync) both the packet name/description and the
    // objective name/description
    async updateSinglePointBasicInfo(
      context,
      payload: {
        trackingId: number;
        packetId: number;
        objective: Objective;
      }
    ) {
      const { commit, state } = orgsActionContext(context);

      commit.upsertRequestStatus({
        trackingId: payload.trackingId,
        status: WebRequestStatus.IN_PROGRESS
      });

      try {
        const [updatedPacket, updatedObjective] = await Promise.all([
          api.secure.updatePacketInfo(
            state.basic.organizationId,
            payload.packetId,
            payload.objective
          ),
          api.secure.updateObjective(payload.packetId, payload.objective)
        ]);

        commit.setOrg(
          state.org
            .updatePacket(payload.packetId, () => updatedPacket)
            .updatePacketObjective(payload.packetId, updatedObjective)
        );

        commit.upsertRequestStatus({
          trackingId: payload.trackingId,
          status: WebRequestStatus.SUCCESS
        });
        await Utils.sleep(1000);
      } catch (error) {
        console.error(error);

        commit.upsertRequestStatus({
          trackingId: payload.trackingId,
          status: WebRequestStatus.FAILURE
        });

        await Utils.sleep(4000);
      }
      commit.upsertRequestStatus({
        trackingId: payload.trackingId,
        status: WebRequestStatus.READY
      });
    },

    // Just wraps insertOutcome
    async createMultiPointIndicator(
      context,
      payload: {
        tempCreateId: number;
        packetId: number;
        index: number;
        value: string;
        color: string;
      }
    ) {
      const { dispatch } = orgsActionContext(context);

      dispatch.insertOutcome({
        ...payload
      });
    },

    async insertOutcome(
      context,
      payload: {
        tempCreateId: number;
        packetId: number;
        index: number;
        value: string;
        color: string;
        iconData?: IconData;
        badgeDescription?: string;
      }
    ) {
      const { commit, state, getters } = orgsActionContext(context);

      commit.upsertCreateStatus({
        tempCreateId: payload.tempCreateId,
        status: WebRequestStatus.IN_PROGRESS
      });

      const outcomes: Partial<Outcome>[] = getters
        .packet(payload.packetId)
        .packet.outcomes.slice();

      outcomes.splice(payload.index, 0, {
        value: payload.value,
        color: payload.color,
        iconData: payload.iconData
      });

      try {
        const {
          outcomes: updatedOutcomes,
          badges
        } = await api.secure.updateOutcomes(
          payload.packetId,
          outcomes,
          payload.badgeDescription
        );

        commit.setOrg(
          state.org.setPacketOutcomes(payload.packetId, updatedOutcomes)
        );

        commit.upsertCreateStatus({
          tempCreateId: payload.tempCreateId,
          status: WebRequestStatus.SUCCESS
        });

        // To get the a typed reference to another module's dispatch,
        // we'd have to introduce circular dependencies.
        badges.forEach(badge =>
          context.dispatch('badges/fetchBadges', badge.objectiveId, {
            root: true
          })
        );

        await Utils.sleep(1000);
      } catch (error) {
        console.error(error);

        commit.upsertCreateStatus({
          tempCreateId: payload.tempCreateId,
          status: WebRequestStatus.FAILURE
        });

        await Utils.sleep(4000);
      }

      commit.upsertCreateStatus({
        tempCreateId: payload.tempCreateId,
        status: WebRequestStatus.READY
      });
    },

    // The api sets each outcome's sortOrder from its place in the list.
    // Shows the new order right away and puts the old one back on failure.
    async reorderOutcomes(
      context,
      payload: { packetId: number; outcomes: Outcome[] }
    ) {
      const { commit, state, getters } = orgsActionContext(context);

      const previous = getters.packet(payload.packetId).packet.outcomes;

      // Keep the stored list in display order too: insertOutcome sends it
      // as-is, so a stale order would undo this one on the next added cog.
      const bySortOrder = (outcomes: Outcome[]) =>
        outcomes.slice().sort((a, b) => a.sortOrder - b.sortOrder);

      commit.setOrg(
        state.org.setPacketOutcomes(
          payload.packetId,
          payload.outcomes.map((o, index) => ({ ...o, sortOrder: index }))
        )
      );

      try {
        const { outcomes } = await api.secure.updateOutcomes(
          payload.packetId,
          payload.outcomes
        );

        commit.setOrg(
          state.org.setPacketOutcomes(payload.packetId, bySortOrder(outcomes))
        );
      } catch (error) {
        console.error(error);

        commit.setOrg(state.org.setPacketOutcomes(payload.packetId, previous));
        commit.errorMessagesAdd('Failed to reorder cogs.');
      }
    },

    async createMultiPointGoal(
      context,
      payload: {
        tempCreateId: number;
        packetId: number;
        name: string;
        description: string;
        iconData: IconData;
      }
    ) {
      const { commit, state, getters } = orgsActionContext(context);

      commit.upsertCreateStatus({
        tempCreateId: payload.tempCreateId,
        status: WebRequestStatus.IN_PROGRESS
      });

      const objectives: Partial<Objective>[] = getters
        .packet(payload.packetId)
        .packet.objectives.slice();

      objectives.push({
        name: payload.name,
        description: payload.description,
        iconData: payload.iconData
      });

      try {
        const {
          objectives: updatedObjectives,
          badges
        } = await api.secure.updateObjectives(payload.packetId, objectives);

        commit.setOrg(
          state.org.setPacketObjectives(payload.packetId, updatedObjectives)
        );

        commit.upsertCreateStatus({
          tempCreateId: payload.tempCreateId,
          status: WebRequestStatus.SUCCESS
        });

        // To get the a typed reference to another module's dispatch,
        // we'd have to introduce circular dependencies.

        badges.forEach(badge =>
          context.dispatch('badges/fetchBadges', badge.objectiveId, {
            root: true
          })
        );

        await Utils.sleep(1000);
      } catch (error) {
        console.error(error);

        commit.upsertCreateStatus({
          tempCreateId: payload.tempCreateId,
          status: WebRequestStatus.FAILURE
        });

        await Utils.sleep(4000);
      }

      commit.upsertCreateStatus({
        tempCreateId: payload.tempCreateId,
        status: WebRequestStatus.READY
      });
    },

    async addPeerGroupPacketConnection(
      context,
      payload: {
        tempAssociateId: number;
        peerGroupId: number;
        packetId: number;
      }
    ) {
      const { commit, state } = orgsActionContext(context);

      commit.upsertAssociateStatus({
        tempAssociateId: payload.tempAssociateId,
        status: WebRequestStatus.IN_PROGRESS
      });

      try {
        await Utils.atLeast(
          api.secure.connectPeerGroupToPacket(
            payload.peerGroupId,
            payload.packetId
          )
        );

        commit.upsertAssociateStatus({
          tempAssociateId: payload.tempAssociateId,
          status: WebRequestStatus.SUCCESS
        });

        await Utils.sleep(1000);

        commit.setOrg(
          state.org.connectGroupToPacket(payload.peerGroupId, payload.packetId)
        );
      } catch (error) {
        console.error(error);

        commit.upsertAssociateStatus({
          tempAssociateId: payload.tempAssociateId,
          status: WebRequestStatus.FAILURE
        });

        await Utils.sleep(4000);
      }
      commit.upsertAssociateStatus({
        tempAssociateId: payload.tempAssociateId,
        status: WebRequestStatus.READY
      });
    },

    async removePacketPeerGroupConnection(
      context,
      payload: {
        tempAssociateId: number;
        peerGroupId: number;
        packetId: number;
      }
    ) {
      const { commit, state } = orgsActionContext(context);

      commit.upsertAssociateStatus({
        tempAssociateId: payload.tempAssociateId,
        status: WebRequestStatus.IN_PROGRESS
      });

      try {
        await api.secure.disconnectPeerGroupFromPacket(
          payload.peerGroupId,
          payload.packetId
        );

        commit.upsertAssociateStatus({
          tempAssociateId: payload.tempAssociateId,
          status: WebRequestStatus.SUCCESS
        });

        await Utils.sleep(1000);

        commit.setOrg(
          state.org.disconnectGroupFromPacket(
            payload.peerGroupId,
            payload.packetId
          )
        );
      } catch (error) {
        console.error(error);

        commit.upsertAssociateStatus({
          tempAssociateId: payload.tempAssociateId,
          status: WebRequestStatus.FAILURE
        });

        await Utils.sleep(4000);
      }
      commit.upsertAssociateStatus({
        tempAssociateId: payload.tempAssociateId,
        status: WebRequestStatus.READY
      });
    },

    // Synchronous
    // Api calls are handled in packetCreator
    addPacket(context, packet: BasePacket) {
      const { commit, state } = orgsActionContext(context);

      commit.setOrg(state.org.addPacket(packet));
    },

    // Not currently used.
    // Could be used to create a standalone (not connected to a peer group)
    // packet creation workflow
    async createPacket(
      context,
      payload: {
        tempCreateId: number;
        peerGroupId: number;
        packet: BasePacket;
      }
    ) {
      const { commit, state } = orgsActionContext(context);

      await makeWebRequest(
        async () => {
          const { packet: newPacket } = await api.secure.createPacket(
            state.basic.organizationId,
            payload.packet
          );

          await api.secure.connectPeerGroupToPacket(
            payload.peerGroupId,
            newPacket.packetId
          );

          return newPacket;
        },
        newPacket =>
          commit.setOrg(
            state.org
              .addPacket(newPacket)
              .connectGroupToPacket(payload.peerGroupId, newPacket.packetId)
          ),
        status =>
          commit.upsertCreateStatus({
            tempCreateId: payload.tempCreateId,
            status
          })
      );
    },

    async updatePacketInfo(
      context,
      payload: {
        trackingId: number;
        packetId: number;
        info: { name: string; description: string };
      }
    ) {
      const { commit, state } = orgsActionContext(context);

      await makeWebRequest(
        () =>
          api.secure.updatePacketInfo(
            state.basic.organizationId,
            payload.packetId,
            payload.info
          ),
        updatedPacket =>
          commit.setOrg(
            state.org.updatePacket(payload.packetId, () => updatedPacket)
          ),
        status =>
          commit.upsertRequestStatus({ trackingId: payload.trackingId, status })
      );
    },

    async deleteOutcome(
      context,
      { packetId, outcomeId }: { packetId: number; outcomeId: number }
    ) {
      const { commit, state } = orgsActionContext(context);

      await makeWebRequest(
        () => api.secure.deleteOutcome(packetId, outcomeId),
        () => {
          commit.setOrg(state.org.deletePacketOutcome(packetId, outcomeId));

          context.commit('badges/removeBadgesForOutcome', outcomeId, {
            root: true
          });
        },
        status => commit.upsertOutcomeStatus({ outcomeId, status })
      );
    },

    async deleteObjective(
      context,
      { packetId, objectiveId }: { packetId: number; objectiveId: number }
    ) {
      const { commit, state } = orgsActionContext(context);

      await makeWebRequest(
        () => api.secure.deleteObjective(packetId, objectiveId),
        () => {
          commit.setOrg(state.org.deletePacketObjective(packetId, objectiveId));

          context.commit('badges/removeBadgesForObjective', objectiveId, {
            root: true
          });
        },
        status => commit.upsertObjectiveStatus({ objectiveId, status })
      );
    },

    async updateOrganization(context, organization: BaseOrganization) {
      const { commit } = orgsActionContext(context);

      await makeWebRequest(
        () => api.secure.updateOrganization(organization),
        result => commit.setBasicInfo(result),
        status => commit.updateOrgUpdateStatus(status)
      );
    },

    async registerLti(
      context,
      payload: {
        openidConfiguration: string;
        registrationToken: string;
      }
    ) {
      const { commit, state } = orgsActionContext(context);

      await makeWebRequest(
        () =>
          api.secure.registerLti(
            state.basic.organizationId,
            payload.openidConfiguration,
            payload.registrationToken
          ),
        () => {
          // No-op as the payload is unimportant
        },
        status => commit.updateLtiRegistrationStatus(status)
      );
    },

    displayErrorMessage(context, message: string) {
      orgsActionContext(context).commit.errorMessagesAdd(message);
    },

    dismissErrorMessage(context, index: number) {
      orgsActionContext(context).commit.errorMessagesRemove(index);
    },

    clear(context) {
      orgsActionContext(context).commit.reset();
    }
  }
});

export default organizations;
